import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { guestOffersFetch } from '../guestOffersApi';
import { newPageViewId, recordSponsoredEvent } from '../commercialSponsoredApi';
import { fetchVenueDetail } from '../venueDetailApi';
import { HopperIcon } from '../ui/icons/HopperIcon';
import './evening-showcase.css';

type Props = {
  city: string;
};

type EveningPlacement = {
  id: string;
  creative_version: number;
  creative_hash: string;
  starts_at: string;
  ends_at: string;
  subject: {
    type: 'venue';
    id: string;
    destination: string;
  };
  title: string;
  description: string;
  media: null | {
    url: string;
    thumbnail_url: string | null;
  };
  venue: {
    id: string;
    name: string;
    address: string | null;
  };
  delivery_token: string;
};

type EveningResponse = {
  data: {
    surface: 'evening_showcase';
    city: { id: string; name: string; timezone: string };
    items: EveningPlacement[];
    served_at: string;
  };
};

const VIEWABILITY_MS = 1000;

function EveningShowcaseCard({ item, avatarUrl }: { item: EveningPlacement; avatarUrl: string | null }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const visibleSentRef = useRef(false);
  const intersectingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleVisible = useCallback(() => {
    clearTimer();
    if (visibleSentRef.current || !intersectingRef.current || document.visibilityState !== 'visible') return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (!intersectingRef.current || document.visibilityState !== 'visible' || visibleSentRef.current) return;
      visibleSentRef.current = true;
      void recordSponsoredEvent(item.delivery_token, 'visible_impression').catch(() => undefined);
    }, VIEWABILITY_MS);
  }, [clearTimer, item.delivery_token]);

  useEffect(() => {
    visibleSentRef.current = false;
    const root = rootRef.current;
    if (!root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      intersectingRef.current = Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.5);
      if (intersectingRef.current) scheduleVisible();
      else clearTimer();
    }, { threshold: [0, 0.5, 1] });
    observer.observe(root);
    return () => {
      observer.disconnect();
      intersectingRef.current = false;
      clearTimer();
    };
  }, [clearTimer, item.id, scheduleVisible]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleVisible();
      else clearTimer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [clearTimer, scheduleVisible]);

  return (
    <article
      ref={(node) => { rootRef.current = node; }}
      className="evening-showcase-card"
      data-evening-showcase-venue={item.venue.id}
      aria-label={item.title}
    >
      <div className="evening-showcase-card__body">
        <div className="evening-showcase-card__identity">
          {avatarUrl ? (
            <img className="evening-showcase-card__avatar" src={avatarUrl} alt="" />
          ) : (
            <div className="evening-showcase-card__avatar evening-showcase-card__avatar--fallback" aria-hidden="true">
              <HopperIcon name="location" size={22} />
            </div>
          )}
          <h3>{item.venue.name ?? item.title}</h3>
        </div>
        <p className="evening-showcase-card__copy">{item.description}</p>
        <a
          className="evening-showcase-card__cta"
          href={item.subject.destination}
          onClick={() => { void recordSponsoredEvent(item.delivery_token, 'open').catch(() => undefined); }}
        >
          Открыть заведение
        </a>
      </div>
    </article>
  );
}

export function EveningShowcaseStrip({ city }: Props) {
  const pageViewId = useMemo(() => newPageViewId(), [city]);
  const [items, setItems] = useState<EveningPlacement[]>([]);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ surface: 'evening_showcase', page_view_id: pageViewId });
    setItems([]);
    setAvatarUrls({});

    void guestOffersFetch<EveningResponse>(`/api/v1/cities/${encodeURIComponent(city)}/commercial/placements?${params.toString()}`, { signal: controller.signal })
      .then(async (response) => {
        const nextItems = response.data.items.slice(0, 3);
        const details = await Promise.allSettled(
          nextItems.map((item) => fetchVenueDetail(item.venue.id, controller.signal)),
        );
        if (controller.signal.aborted) return;

        const nextAvatarUrls: Record<string, string | null> = {};
        details.forEach((result, index) => {
          const item = nextItems[index];
          if (!item) return;
          nextAvatarUrls[item.venue.id] = result.status === 'fulfilled' ? result.value.venue.avatar_url : null;
        });
        setAvatarUrls(nextAvatarUrls);
        setItems(nextItems);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setItems([]);
          setAvatarUrls({});
        }
      });
    return () => controller.abort();
  }, [city, pageViewId]);

  if (items.length === 0) return null;

  return (
    <section className="evening-showcase" aria-label="Сегодня вечером">
      <div className="evening-showcase__heading">
        <span>Сегодня вечером</span>
      </div>
      <div className="evening-showcase__track">
        {items.map((item) => (
          <EveningShowcaseCard
            item={item}
            avatarUrl={avatarUrls[item.venue.id] ?? null}
            key={`${item.id}:${item.delivery_token}`}
          />
        ))}
      </div>
    </section>
  );
}
